/**
 * One place that answers "what does THIS insurer want?".
 *
 * The company checks used to be inline string comparisons scattered across
 * BasicPolicyDetailsStep, CustomerDetailsStep, FormNavigationButtons and
 * validationSchemas — and, worse, several were written as
 * `company === "national" ? nationalList : relianceList`, so every insurer
 * that was not National silently inherited Reliance's rules. That is exactly
 * how KSHEMA ended up with Reliance's nominee relations but not Reliance's
 * nominee fields.
 *
 * Every predicate below is explicit: adding insurer #4 means adding it to the
 * lists here, not hunting for ternaries.
 */
import {
  relianceNomineeRelations,
  nationalNomineeRelations,
  kshemaNomineeRelations,
  briskNomineeRelations,
  genderOptions,
} from "../constants/formOptions";
import { WALLET_INSURERS } from "../../../../Shared/CommonConstant";

/** Company values are typed by hand into the Make & Model master, so casing
 *  and stray whitespace are not guaranteed. Normalise before every compare. */
const norm = (company) => String(company || "").trim().toLowerCase();

/**
 * Every insurer this module knows the rules for.
 *
 * Written the way the Make & Model master writes them, because that is the
 * value stored on the policy and therefore what a company filter has to match.
 * Adding insurer #4 means adding it here and to the predicates below.
 */
export const SUPPORTED_COMPANIES = ["Reliance", "National", "KSHEMA"];

export const isReliance = (company) => norm(company) === "reliance";
export const isNational = (company) => norm(company) === "national";
export const isKshema = (company) => norm(company) === "kshema";

/** The Nominee Relation dropdown. Each insurer's portal accepts only its own
 *  wording, so an unknown company gets Reliance's list as a last resort rather
 *  than an empty dropdown. */
export const nomineeRelationsFor = (company) => {
  if (isNational(company)) return nationalNomineeRelations;
  if (isKshema(company)) return kshemaNomineeRelations;
  return relianceNomineeRelations;
};

// ── Which nominee fields each insurer asks for ──────────────────────────────
// Reliance: Name, Relation, Gender, DOB, Appointee
// National: Name, Relation, No of Year, Nominee Age, Appointee
// KSHEMA:   Name, Type, Relation, Share, DOB

/** Nominee Gender — Reliance only. */
export const hasNomineeGender = (company) => isReliance(company);

/** Nominee Date of Birth — Reliance and KSHEMA. National asks for a plain age
 *  instead, so a DOB there would be collected and never submitted. */
export const hasNomineeDob = (company) =>
  isReliance(company) || isKshema(company);

/** Nominee Age and the 1/5 year PA term — National only. */
export const hasNomineeAge = (company) => isNational(company);
export const hasPaCoverYears = (company) => isNational(company) || isKshema(company);

/** Nominee Type (Individual / Corporate) and Nominee Share — KSHEMA only. */
export const hasNomineeType = (company) => isKshema(company);
export const hasNomineeShare = (company) => isKshema(company);

/** Appointee Name. Not on KSHEMA's proposal, so requiring it there would block
 *  a submission on a field the portal never asks for. */
export const hasAppointeeName = (company) => !isKshema(company);

/** The free-text "Specify Other Relation" box, shown when the chosen relation
 *  is "Others". Reliance only: KSHEMA's "Other" is picked as it is (the owner,
 *  2026-11-05: "for KSHEMA the specify other field is not wanted"), and
 *  National's list has no such entry. */
export const hasOtherRelationName = (company) => isReliance(company);

/** True when `relation` is one of the catch-all "other" entries. */
export const isOtherRelation = (relation) =>
  ["other", "others"].includes(norm(relation));

/** Aadhaar upload: Reliance rejects a single merged scan, so the front and
 *  back must be uploaded as two separate files and both are mandatory. */
export const requiresSeparateAadhar = (company) => isReliance(company);

// ── Company routing (`settings`) ────────────────────────────────────────────
//
// One object says how a policy is split between insurers:
//
//   settings: { isMultipleCompany, PACompany, ODCompany, TPCompany }
//
// PACompany is "Brisk", an insurer name, or "" when PA Cover is off. The older
// flat fields (company / odCompany / tpCompany / paCoverCompany /
// paCoverInsurerCompany) are still sent — every automation reader uses them —
// but they are DERIVED from `settings` (settingsToLegacyFields), never picked
// separately. This block mirrors RayalBrokers-backend/shared/policySettings.js;
// keep the derivation rules in the two identical.

export const BRISK = "Brisk";
const GST_MULTIPLIER = 1.18;
const truthy = (v) => v === true || norm(v) === "true";
const round2 = (n) => Number(Number(n).toFixed(2));

/** "reliance" -> "Reliance" (the wallet spelling); unknown names are trimmed. */
export const canonicalCompany = (name) => {
  const key = norm(name);
  if (!key) return "";
  return WALLET_INSURERS.find((c) => c.toLowerCase() === key) || String(name).trim();
};

export const sameCompany = (a, b) => !!norm(a) && norm(a) === norm(b);

/**
 * What a policy covers: "both" (OD + TP — one insurer sells both, or two insurers sell one each; as always),
 * "od" (ONLY the own-damage policy) or "tp" (ONLY the third-party policy), the latter two from ONE insurer.
 * Mirrors RayalBrokers-backend/shared/policySettings.js coverageOf — keep the two identical. `coverage` is a field of
 * its own, NOT part of `settings`.
 */
export const COVERAGES = ["both", "od", "tp"];
export const coverageOf = (values) => {
  const v = norm(values?.coverage);
  if (COVERAGES.includes(v)) return v;
  // Only a schema-v2 policy's od/tp selection is authoritative.
  if (Number(values?.schemaVersion) !== 2) return "both";
  const od = values.odSelected !== false;
  const tp = values.tpSelected !== false;
  return od && !tp ? "od" : tp && !od ? "tp" : "both";
};

/**
 * The customer's EXISTING third-party policy, which Reliance's own-damage product asks for (2026-10-22): number,
 * insurer, start and end date. On the FORM: "required" for OD only (sold by Reliance only), null otherwise. A policy
 * sold by two insurers does not ask it on the form (the owner, 2026-10-23): the TP policy is bought by us, so its
 * details exist only once the TP part is finished — then they are entered in Edit -> Status / TP policy details
 * (`tpDetailsOpen`). Mirrors RayalBrokers-backend/shared/existingTpPolicy.js.
 */
export const EXISTING_TP_INSURERS = ["KSHEMA", "National"];
export const existingTpMode = (values) => (values?.companyChoice !== "multiple" && coverageOf(values) === "od" ? "required" : null);
/** The same for a SAVED policy (no `companyChoice`): OD only, or two insurers by its routing. */
export const existingTpModeOfPolicy = (policy) => {
  const coverage = coverageOf(policy);
  if (coverage === "od") return "required";
  return coverage === "both" && buildPolicySettings(policy || {}, { multiProduct: true }).isMultipleCompany ? "optional" : null;
};
const TP_DONE = ["completed", "completed_with_errors"];
const lower = (v) => String(v || "").trim().toLowerCase();
/**
 * Is the TP part of a two-insurer policy finished (the TP policy issued)? Its job part is done, or its Proposal, KYC and
 * Payment are all Completed — the server's rule (shared/existingTpPolicy.js tpFinished). Reads a list row / the View
 * page's policy: `parts`, `relianceStageView` (or the raw `relianceStages`).
 */
export const tpPartFinished = (policy) => {
  if ((policy?.parts || []).some((p) => p && p.key === "tp" && TP_DONE.includes(lower(p.status)))) return true;
  const steps = ["proposal", "kyc", "payment"];
  const view = (Array.isArray(policy?.relianceStageView) ? policy.relianceStageView : []).find((p) => p && p.key === "tp");
  if (view && steps.every((s) => lower(((view.stages || []).find((r) => r.stage === s) || {}).status) === "completed")) return true;
  const raw = policy?.relianceStages?.tp;
  return !!raw && steps.every((s) => lower(raw[s] && raw[s].status) === "completed");
};
/** A two-insurer policy's TP details are shown / entered only once the TP part is finished (or one was already saved). */
export const tpDetailsOpen = (policy) => tpPartFinished(policy) || !!String(policy?.existingTpPolicyNumber || "").trim();

/** The insurer to SHOW for the existing TP policy: the one typed (OD only) or the policy's TP company (two insurers). */
export const existingTpInsurerOf = (values, mode) =>
  mode === "optional" ? routingOf(values).TPCompany || values?.existingTpInsurer || "" : values?.existingTpInsurer || "";

/** A TP policy number: letters, digits and / - . space, 3 to 40 characters (the server checks the same). */
export const isTpPolicyNumber = (value) => /^[A-Z0-9][A-Z0-9/\-. ]{1,38}[A-Z0-9]$/.test(String(value || "").trim().toUpperCase());

/** The od/tp selection a coverage means: { odSelected, tpSelected }. */
export const selectionOfCoverage = (coverage) => ({
  odSelected: coverage !== "tp",
  tpSelected: coverage !== "od",
});

/**
 * What the PA Cover Company dropdown offers. Brisk always; the OD company;
 * and — only when the server runs the multi-product (OD/TP as separate jobs)
 * flow — the TP company too. With that flow off a policy is ONE bundled job
 * sold by the OD company, so it is the only insurer that can sell PA.
 */
export const paCompanyOptions = (settings, { multiProduct = false } = {}) => {
  const out = [BRISK];
  const add = (c) => {
    if (c && !out.some((o) => sameCompany(o, c))) out.push(c);
  };
  add(settings?.ODCompany);
  if (multiProduct) add(settings?.TPCompany);
  return out;
};

/**
 * What the PA Cover Company dropdown OFFERS. Brisk always. For a policy sold by
 * two insurers (the multi-product flow) the TP insurer only: PA Cover is not
 * taken on the OD insurer's policy. Otherwise — one insurer, or one bundled
 * job — exactly what paCompanyOptions allows.
 *
 * Narrower than paCompanyOptions on purpose. That one says what a policy may
 * CARRY (the server mirrors it), and it still has to accept a policy that was
 * sold with PA on its OD insurer. `saved` is such a policy's own PA company:
 * it stays on the list for that policy, so an edit shows what was sold.
 */
export const paCompanyOffered = (settings, { multiProduct = false, saved = "", brisk = true } = {}) => {
  // `brisk: false` (server BRISK_PA_COVER_ENABLED=false): Brisk is not offered, except as a saved policy's own PA company.
  const allowed = paCompanyOptions(settings, { multiProduct }).filter(
    (c) => brisk || !sameCompany(c, BRISK) || sameCompany(c, saved)
  );
  const twoInsurers =
    !!settings?.ODCompany && !!settings?.TPCompany && !sameCompany(settings.ODCompany, settings.TPCompany);
  const out =
    multiProduct && twoInsurers ? allowed.filter((c) => !sameCompany(c, settings.ODCompany)) : allowed;
  const kept = allowed.find((c) => sameCompany(c, saved));
  if (kept && !out.some((c) => sameCompany(c, kept))) out.push(kept);
  return out;
};

/**
 * Canonical settings from form values (or a saved policy): a `settings`
 * object if one is there, else the legacy flat fields. Always a full object.
 */
export const buildPolicySettings = (values, { multiProduct = false } = {}) => {
  const p = values || {};
  const s = p.settings && typeof p.settings === "object" ? p.settings : {};

  const ODCompany = canonicalCompany(s.ODCompany || p.odCompany || p.company);
  const TPCompany = canonicalCompany(s.TPCompany || p.tpCompany || p.company || ODCompany);
  const isMultipleCompany = !!ODCompany && !!TPCompany && !sameCompany(ODCompany, TPCompany);

  let PACompany = "";
  if (truthy(p.paCover)) {
    const options = paCompanyOptions({ ODCompany, TPCompany }, { multiProduct });
    const fromSettings = canonicalCompany(s.PACompany);
    if (fromSettings && options.some((o) => sameCompany(o, fromSettings))) {
      PACompany = fromSettings;
    } else {
      const through = norm(p.paCoverCompany);
      if (through === "brisk") {
        PACompany = BRISK;
      } else {
        const named = canonicalCompany(p.paCoverInsurerCompany);
        const throughAsInsurer =
          through && through !== "company" ? canonicalCompany(p.paCoverCompany) : "";
        const pick = [throughAsInsurer, named].find(
          (c) => c && options.some((o) => sameCompany(o, c) && !sameCompany(o, BRISK))
        );
        PACompany = pick || ODCompany;
      }
    }
  }
  return { isMultipleCompany, PACompany, ODCompany, TPCompany };
};

/** The flat fields the automation and reports read, derived from settings. */
export const settingsToLegacyFields = (settings, values) => {
  const out = {
    company: settings.ODCompany || "",
    odCompany: settings.ODCompany || "",
    tpCompany: settings.TPCompany || "",
  };
  if (truthy(values?.paCover)) {
    const viaBrisk = sameCompany(settings.PACompany, BRISK);
    out.paCoverCompany = viaBrisk ? BRISK : "Company";
    out.paCoverInsurerCompany = viaBrisk ? "" : settings.PACompany || "";
  } else {
    out.paCoverInsurerCompany = "";
  }
  return out;
};

/**
 * The routing fields to load into the form from a SAVED policy — used by both
 * edit loaders so they cannot drift apart. Reads the policy's own `settings`
 * (falling back to its flat company fields for policies saved before it
 * existed) and returns `settings` plus the flat fields derived from it.
 *
 * Built tolerant of the multi-company flag (`multiProduct: true`): a saved
 * PACompany is the insurer the policy was SOLD with and is kept as-is, even if
 * the flag has since been switched off.
 */
export const routingFromPolicy = (policy) => {
  const settings = buildPolicySettings(policy, { multiProduct: true });
  const flat = settingsToLegacyFields(settings, policy);
  const coverage = coverageOf(policy);
  return {
    settings,
    coverage,
    // OD only / TP only: the selection follows the cover (a saved v1 document has both flags false by default).
    odSelected: coverage === "both" ? policy?.odSelected ?? true : coverage === "od",
    tpSelected: coverage === "both" ? policy?.tpSelected ?? true : coverage === "tp",
    company: flat.company || policy?.company || "",
    odCompany: flat.odCompany,
    tpCompany: flat.tpCompany,
    paCoverCompany: truthy(policy?.paCover)
      ? flat.paCoverCompany
      : policy?.paCoverCompany || BRISK,
    paCoverInsurerCompany: flat.paCoverInsurerCompany,
  };
};

/** "Reliance" for one insurer, "Reliance (OD) · National (TP)" for two. */
export const describeCompanies = (policy) => {
  const r = routingOf(policy);
  if (!r.ODCompany) return "";
  if (r.isMultipleCompany) return `${r.ODCompany} (OD) · ${r.TPCompany} (TP)`;
  const coverage = coverageOf(policy);
  return coverage === "od" ? `${r.ODCompany} (OD only)` : coverage === "tp" ? `${r.TPCompany} (TP only)` : r.ODCompany;
};

/** "Brisk" / "Reliance" — who provides PA Cover; "" when PA is off. */
export const describePaCompany = (policy) =>
  truthy(policy?.paCover) ? routingOf(policy).PACompany || "" : "";

/** The routing for a form's values — the stored `settings` when set, else derived. */
export const routingOf = (values, opts) =>
  values?.settings?.ODCompany ? values.settings : buildPolicySettings(values, opts);

/** True when the OD or TP insurer satisfies `predicate` (isNational, isKshema …). */
export const hasCompanyLeg = (values, predicate) => {
  const r = routingOf(values);
  return !!(predicate(r.ODCompany) || predicate(r.TPCompany));
};

/**
 * True when EVERY insurer of the policy is KSHEMA (a one-insurer KSHEMA policy; both legs KSHEMA). A two-insurer policy that
 * has one KSHEMA leg is not "KSHEMA only": its other insurer still needs the full financier details.
 */
export const isKshemaOnlyPolicy = (values) => {
  const r = routingOf(values);
  return !!r.ODCompany && isKshema(r.ODCompany) && isKshema(r.TPCompany || r.ODCompany);
};

/**
 * KSHEMA's Financier Name is a SEARCH dropdown on its portal, and the list does not open for a single character — the
 * automation would type one letter and wait for a list that never comes. So a name for a KSHEMA policy needs at least this many
 * letters (spaces do not count). The automation checks the same number (Rayal-policy-form-automation kshemaFinancier.js,
 * MIN_NAME_LETTERS) for a policy saved before this rule existed.
 */
export const KSHEMA_FINANCIER_NAME_MIN_LETTERS = 2;

/** A name was typed but is shorter than KSHEMA's search takes. Empty is not "too short" — that is the "required" rule's message. */
export const isFinancierNameTooShort = (name) => {
  const letters = String(name == null ? "" : name).replace(/\s+/g, "").length;
  return letters > 0 && letters < KSHEMA_FINANCIER_NAME_MIN_LETTERS;
};

/** The insurer billing PA Cover, or "" when PA is off or goes through Brisk. */
export const paInsurerOf = (values) => {
  const pa = routingOf(values).PACompany;
  return pa && !sameCompany(pa, BRISK) ? pa : "";
};

/**
 * PA Cover bought from BRISK on a policy sold by two insurers or TP only (the owner, 2026-10-23: "the PA is done only with the
 * company that is selected"). Brisk's certificate IS the cover — no portal buys PA, KSHEMA's / National's own PA is off — so the
 * nominee is the one Brisk takes: Name, Gender and a Relation from Brisk's own eight. A single-company policy keeps the layout of
 * its insurer, as it always had.
 */
export const isBriskNominee = (values) => {
  if (!truthy(values?.paCover)) return false;
  const routing = routingOf(values);
  if (!sameCompany(routing.PACompany, BRISK)) return false;
  return routing.isMultipleCompany === true || coverageOf(values) === "tp";
};

/**
 * Whose nominee questions the PA Cover Details section asks. The PA insurer
 * when there is one; otherwise National if either leg is National (it demands
 * a nominee on every quote); then Brisk itself for a Brisk PA on two insurers /
 * TP only; otherwise the OD company — which is what the section always used
 * before the routing was split.
 */
export const nomineeCompanyOf = (values) => {
  const insurer = paInsurerOf(values);
  if (insurer) return insurer;
  if (hasCompanyLeg(values, isNational)) return "National";
  if (isBriskNominee(values)) return BRISK;
  return routingOf(values).ODCompany || values?.company || "";
};

/**
 * Which nominee fields the PA Cover Details section shows and requires — ONE answer for the form and its validation, so they
 * can never disagree. `company` is whose layout (nomineeCompanyOf); `brisk` is a Brisk PA on two insurers / TP only.
 *   Reliance: Name, Relation, Gender, DOB, Appointee      National: Name, Relation, No of Year, Age, Appointee
 *   KSHEMA:   Name, Type, Relation, Share, DOB, No of Year      Brisk (two insurers / TP only): Name, Gender, Relation (Brisk's eight)
 * With a Brisk PA and a National TP the National layout stays (it demands a nominee on every quote: Age, and the year that
 * sets the TP end date) plus Gender, minus the Appointee (the portal's PA is "No"), and the Relation list is Brisk's.
 */
export const nomineeFieldsOf = (values) => {
  const company = nomineeCompanyOf(values);
  const brisk = isBriskNominee(values);
  return {
    company,
    brisk,
    gender: brisk || hasNomineeGender(company),
    dob: hasNomineeDob(company),
    age: hasNomineeAge(company),
    years: hasPaCoverYears(company),
    type: hasNomineeType(company),
    share: hasNomineeShare(company),
    appointee: !brisk && hasAppointeeName(company),
    otherRelation: hasOtherRelationName(company),
    relations: brisk ? briskNomineeRelations : nomineeRelationsFor(company),
    // Brisk takes Male or Female only
    genders: brisk ? genderOptions.filter((g) => g.value !== "other") : genderOptions,
  };
};

/**
 * The motor premium WITHOUT PA Cover, GST included. The calculator folds the
 * PA amount into Total B (PA to Owner Driver), so its finalPremium already
 * contains PA + 18% GST; the wallet, the Preview and the payload all charge PA
 * separately, so it has to come back out first.
 */
export const motorPremiumExcludingPa = (calculatedFinalPremium, values) => {
  const total = Number(calculatedFinalPremium);
  if (!Number.isFinite(total)) return undefined;
  // OD only: the PA amount is never passed to the engine (it sits in Total B, which an OD-only policy does not have),
  // so the premium holds no PA and there is nothing to take back out.
  if (coverageOf(values) === "od") return round2(total);
  const paWithGst = truthy(values?.paCover)
    ? (Number(values?.paCoverAmount) || 0) * GST_MULTIPLIER
    : 0;
  return round2(total - paWithGst);
};

const LEG_LABELS = {
  od: "OD Premium",
  tp: "TP Premium",
  motor: "Motor Premium",
  pa: "CPA (With GST)",
};
const LEG_PARTS = { od: "OD", tp: "TP", motor: "Motor", pa: "CPA" };

/**
 * How the premium divides between the insurers that sell it — the same rule as
 * the backend's buildPremiumSplit (shared/policySettings.js), so what the
 * screens show is exactly what the wallet debits.
 *
 *   OD  leg: net odNet,          total = round2(net × 1.18)
 *   TP  leg: net tpNet,          total = motor premium − OD total (absorbs the
 *                                rupee rounding, so the legs always add up)
 *   CPA leg: net paCoverAmount,  total = round2(net × 1.18)
 *
 * isSplit (mode "split") only when the multi-product flow is on and OD ≠ TP:
 * each leg is then sold by its own insurer. Otherwise every leg belongs to the
 * policy's own company (PA to Brisk when chosen) — as it always has.
 *
 * Every leg also carries `charge`, the amount its insurer's wallet is debited
 * (the same field the backend's buildPremiumSplit sets): the calculator's own
 * net figure for a two-company policy on the "net" basis, the GST-inclusive
 * `total` otherwise. `total` is unchanged, so the legs still add up to the
 * Final Premium.
 *
 * @param {Object} input  company / settings / flat routing fields, paCover,
 *   paCoverAmount, motorPremium (ex-PA, GST included), odNet, tpNet
 * @param {Object} [opts] `chargeBasis`: "net" (default, every new policy) or
 *   "gross" (a split saved before the net basis existed; see chargeBasisOf)
 * @returns {{ isSplit, mode, legs, byCompany, grandTotal, chargeTotal }}
 */
export const premiumSplit = (
  input,
  { multiProduct = false, chargeBasis = "net" } = {}
) => {
  const p = input || {};
  const settings = buildPolicySettings(p, { multiProduct });

  const motorPremium = round2(Number(p.motorPremium) || 0);
  const paOn = truthy(p.paCover);
  const paNet = paOn ? round2(Number(p.paCoverAmount) || 0) : 0;
  const paTotal = round2(paNet * GST_MULTIPLIER);

  const odNet = Number(p.odNet);
  const tpNetRaw = Number(p.tpNet);
  const hasBreakup = Number.isFinite(odNet) && odNet > 0;
  const odTotal = hasBreakup ? round2(odNet * GST_MULTIPLIER) : 0;
  const tpTotal = hasBreakup ? round2(motorPremium - odTotal) : 0;
  const breakupAddsUp = hasBreakup && odTotal <= motorPremium && tpTotal >= 0;

  // A part-policy: ONE product from ONE insurer (coverage "od" / "tp") — one motor leg keyed od / tp, never `motor`.
  const coverage = coverageOf(p);
  const partKey = coverage !== "both" && !settings.isMultipleCompany ? coverage : "";
  const mode = partKey
    ? "part"
    : multiProduct && settings.isMultipleCompany && breakupAddsUp
      ? "split"
      : "single";

  const policyCompany = canonicalCompany(p.company) || settings.ODCompany;
  const sellerOf = (key) => {
    if (key === "pa") {
      if (mode === "split") return sameCompany(settings.PACompany, BRISK) ? BRISK : settings.PACompany;
      const viaBrisk = p.settings?.PACompany
        ? sameCompany(settings.PACompany, BRISK)
        : norm(p.paCoverCompany) === "brisk";
      return viaBrisk ? BRISK : policyCompany;
    }
    if (mode === "split") return key === "tp" ? settings.TPCompany : settings.ODCompany;
    return policyCompany;
  };

  const chargeOnNet = (mode === "split" || mode === "part") && chargeBasis !== "gross";
  const leg = (key, net, total) => ({
    key,
    label: LEG_LABELS[key],
    company: sellerOf(key),
    net: net === null ? null : round2(net),
    gst: net === null ? null : round2(total - net),
    total: round2(total),
    charge: round2(chargeOnNet && net !== null ? net : total),
  });

  // The net of the one product that is sold: the calculator's figure, else the motor premium taken back out of GST.
  const partNetSent = partKey === "od" ? odNet : tpNetRaw;
  const partNet = Number.isFinite(partNetSent) && partNetSent > 0 ? partNetSent : round2(motorPremium / GST_MULTIPLIER);

  const legs = partKey
    ? [leg(partKey, partNet, motorPremium)]
    : breakupAddsUp
      ? [
          leg("od", odNet, odTotal),
          leg("tp", Number.isFinite(tpNetRaw) ? tpNetRaw : round2(tpTotal / GST_MULTIPLIER), tpTotal),
        ]
      : [leg("motor", null, motorPremium)];
  if (paOn) legs.push(leg("pa", paNet, paTotal));

  const byCompany = [];
  for (const l of legs) {
    let entry = byCompany.find((c) => sameCompany(c.company, l.company));
    if (!entry) {
      entry = { company: l.company, total: 0, charge: 0, parts: [] };
      byCompany.push(entry);
    }
    entry.total = round2(entry.total + l.total);
    entry.charge = round2(entry.charge + l.charge);
    entry.parts.push(LEG_PARTS[l.key]);
  }

  return {
    isSplit: mode === "split",
    mode,
    legs,
    byCompany,
    grandTotal: round2(legs.reduce((sum, l) => sum + l.total, 0)),
    chargeTotal: round2(legs.reduce((sum, l) => sum + l.charge, 0)),
  };
};

/**
 * The charge basis a SAVED policy was sold with — the same rule as the
 * backend's chargeBasisOf. "net" only when its saved split says so; a split
 * saved before the basis existed was debited with GST, so it stays "gross"
 * (an edit refunds and re-charges against what was really taken).
 */
export const chargeBasisOf = (policy) => {
  const stored = policy?.premiumCalculation?.split;
  if (!stored) return "net";
  if (stored.chargeBasis === "net") return "net";
  return stored.mode === "split" ? "gross" : "net";
};

/**
 * The split for the live form, straight from the calculator result. The
 * engine's Total B carries PA as "PA to Owner Driver", so it is taken out to
 * get the TP net; the motor premium is the final premium minus PA with GST.
 */
export const splitFromCalculation = (calculationResult, values, opts) => {
  if (!calculationResult) return null;
  const odNet = Number(calculationResult.totalAodPremium);
  const tpNet =
    (Number(calculationResult.totalBodPremium) || 0) -
    (Number(calculationResult.pAOwnerDriver) || 0);
  return premiumSplit(
    {
      ...values,
      motorPremium: motorPremiumExcludingPa(calculationResult.finalPremium, values),
      odNet,
      tpNet: round2(tpNet),
    },
    opts
  );
};

/**
 * The split a SAVED policy was sold with — the server stores it on every save
 * (premiumCalculation.split). Null for a policy saved before that existed.
 */
export const splitFromPolicy = (policy) => {
  const stored = policy?.premiumCalculation?.split;
  if (!stored || !Array.isArray(stored.legs) || stored.legs.length === 0) return null;
  const legs = stored.legs.map((l) => ({ ...l, label: l.label || LEG_LABELS[l.key] || l.key }));
  return {
    isSplit: stored.mode === "split",
    mode: stored.mode,
    legs,
    byCompany: Array.isArray(stored.byCompany) ? stored.byCompany : [],
    grandTotal:
      Number(stored.grandTotal) || round2(legs.reduce((sum, l) => sum + (Number(l.total) || 0), 0)),
  };
};

/** "OD", "TP + CPA" — which parts a company sells, for "Pay to" lines. */
export const describeParts = (parts) => (parts || []).join(" + ");

/**
 * Which Company Wallet(s) a policy will debit and how much — built on
 * premiumSplit, the same rule as the backend's resolveWalletRequirements, so
 * the balance check shows exactly what will be taken. Returns
 * [{ company, amount, labels }], one entry per wallet.
 *
 * `data` carries: company, paCover, paCoverAmount, paCoverCompany, settings,
 * premiumCalculation.odPremium / tpPremium and the motor premium (ex-PA) as
 * `motorPremium`.
 *
 * `excludeLegs` (od / tp / motor / pa) leaves out legs that belong to parts
 * already bought: an edit freezes those on the server — no refund, no re-debit.
 */
export const walletRequirementsFor = (
  data,
  { multiProduct = false, excludeLegs = [], chargeBasis = "net", onlyBriskPa = false } = {}
) => {
  const walletOf = (name) =>
    WALLET_INSURERS.find((c) => c.toLowerCase() === norm(name)) || null;
  const split = premiumSplit(
    {
      ...data,
      odNet: data?.premiumCalculation?.odPremium,
      tpNet: data?.premiumCalculation?.tpPremium,
    },
    { multiProduct, chargeBasis }
  );

  const byCompany = {};
  const add = (company, amount, label) => {
    if (!amount || amount <= 0) return;
    const key = company || "__unresolved__";
    if (!byCompany[key]) byCompany[key] = { company, amount: 0, labels: [] };
    byCompany[key].amount = round2(byCompany[key].amount + amount);
    byCompany[key].labels.push(label);
  };

  const legs = split.legs.filter(
    (l) =>
      !excludeLegs.includes(l.key) &&
      (!onlyBriskPa || (l.key === "pa" && norm(l.company) === "brisk"))
  );
  // A part-policy (OD only / TP only) is debited per leg like a split: one leg, at the calculator's net figure.
  if (split.isSplit || split.mode === "part") {
    const names = { od: "Motor Premium (OD)", tp: "Motor Premium (TP)", pa: "PA Cover" };
    legs.forEach((l) => add(walletOf(l.company), l.charge, names[l.key]));
  } else {
    const motor = round2(
      legs.filter((l) => l.key !== "pa").reduce((sum, l) => sum + l.charge, 0)
    );
    add(walletOf(data?.company), motor, "Motor Premium");
    const pa = legs.find((l) => l.key === "pa");
    if (pa) add(walletOf(pa.company), pa.charge, "PA Cover");
  }
  return Object.values(byCompany);
};

/** Whether the PAN card upload is offered at all.
 *
 *  KSHEMA takes Aadhaar only, so the upload is hidden there rather than left
 *  on screen as an optional slot nobody should fill. Reliance and National
 *  keep it — it has always been optional for them, and removing it would
 *  quietly drop a document their operators do attach. */
export const hasPanCard = (company) => !isKshema(company);
